import React from "react";
import { RichMath, BlockMath } from "./KaTeX";
import RichText from "./RichText";
import { ZoomIn } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LEGACY_PATH_RE = /^[\w.-]+\/questions\/[\w-]+\/[0-9a-f]{32}\.(png|jpe?g|gif|webp)$/i;

// Image block path: attached image_path, or legacy editor upload stored in value.
// Import descriptions ("icerik") in value are NOT paths and render nothing.
export function imageBlockPath(b) {
  if (b?.image_path) return b.image_path;
  const v = (b?.value || "").trim();
  return LEGACY_PATH_RE.test(v) ? v : null;
}

/**
 * Renders question rich content blocks in the admin-defined order.
 * Falls back to legacy text/latex/image_path fields for backwards compatibility.
 */
export function QuestionContent({ question }) {
  const blocks = Array.isArray(question?.blocks) ? question.blocks : [];
  if (blocks.length === 0) {
    // Legacy render: text + optional latex + optional image
    return (
      <div className="space-y-3">
        {question?.text && <div className="text-lg leading-relaxed"><RichMath text={question.text} /></div>}
        {question?.latex && <div className="mt-1"><BlockMath>{question.latex}</BlockMath></div>}
        {question?.image_path && (
          <QuestionImage path={question.image_path} alt="soru gorseli" />
        )}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {blocks.map((b, i) => {
        if (b.type === "text") {
          return (
            <div key={i} className="text-lg leading-relaxed">
              <RichText html={b.value || ""} />
            </div>
          );
        }
        if (b.type === "latex") {
          return (
            <div key={i}>
              <BlockMath>{b.value || ""}</BlockMath>
            </div>
          );
        }
        if (b.type === "image") {
          return <QuestionImage key={i} path={imageBlockPath(b)} alt={`gorsel-${i + 1}`} />;
        }
        return null;
      })}
    </div>
  );
}

export function QuestionImage({ path, alt = "gorsel" }) {
  if (!path) return null;
  const src = `${BACKEND_URL}/api/uploads/file?path=${encodeURIComponent(path)}`;
  return (
    <a href={src} target="_blank" rel="noreferrer" className="inline-block group relative">
      <img alt={alt} src={src}
           className="max-h-72 rounded-lg border border-slate-100 mx-auto block group-hover:opacity-95" />
      <span className="absolute right-2 bottom-2 rounded-full bg-black/60 text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <ZoomIn size={14} />
      </span>
    </a>
  );
}

/** Choice value renderer: optional image + text + optional latex. */
export function ChoiceContent({ choice }) {
  if (!choice) return null;
  return (
    <div className="flex-1">
      {choice.image_path && (
        <img
          alt={`secenek-${choice.key}`}
          src={`${BACKEND_URL}/api/uploads/file?path=${encodeURIComponent(choice.image_path)}`}
          className="max-h-24 rounded border border-slate-100 mb-1"
        />
      )}
      {choice.text ? <RichMath text={choice.text} /> : null}
      {choice.latex ? <span className="ml-1"><RichMath text={`$${choice.latex}$`} /></span> : null}
    </div>
  );
}
